import moment from 'moment';

/**
 * accepts javascript new Date and converts to YYYY/MM/DD
 * @param  {Date}    date
 * @param  {Boolean} isUTC
 * @return {String}  YYYY/MM/DD
 */
export const getFormattedDate = (date, isUTC = false) => {
  if (isUTC) {
    return moment.unix(date).utc().format('YYYY-MM-DD');
  }

  let year = date.getFullYear();
  let month = (1 + date.getMonth()).toString().padStart(2, '0');
  let day = date.getDate().toString().padStart(2, '0');

  return year + '-' + month + '-' + day;
};

/**
 * formats a Date as MM/DD/YYYY for display in the date inputs.
 * @param  {Date|null} date
 * @return {String}    MM/DD/YYYY, or '' when there is no date
 */
export const getFormattedDisplayDate = (date) => (date ? moment(date).format('MM/DD/YYYY') : '');

const STRICT_TYPING_FORMATS = ['MM/DD/YYYY', 'M/D/YYYY', 'MMDDYYYY'];
const LENIENT_BLUR_FORMATS = [
  'MM/DD/YYYY',
  'M/D/YYYY',
  'MM/D/YYYY',
  'M/DD/YYYY',
  'MM/DD/YY',
  'M/DD/YY',
  'MM/D/YY',
  'M/D/YY',
  'MMDDYYYY',
];

/**
 * whether a typed string is already a complete, valid date (4-digit year).
 * Used to decide whether typing should commit vs. wait for more input.
 * @param  {String} dateString
 * @return {Boolean}
 */
export const isCompleteDate = (dateString) =>
  moment((dateString || '').trim(), STRICT_TYPING_FORMATS, true).isValid();

/**
 * lenient parse for committing on blur. Accepts short forms (1/1/2025) and
 * 2-digit years (1/1/25); returns a Date, or null when unparseable.
 * @param  {String} dateString
 * @return {Date|null}
 */
export const parseFlexibleDate = (dateString) => {
  const trimmed = (dateString || '').trim();
  if (!trimmed) return null;

  const parsed = moment(trimmed, LENIENT_BLUR_FORMATS, true);
  return parsed.isValid() ? parsed.toDate() : null;
};

/**
 * accepts javascript new Date and converts to MMM DD, YYYY
 * @param  {Date}    date
 * @return {String}  MMM DD, YYYY
 */
export const getFormattedLongDate = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) return 'N/A';

  return date.toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

/**
 * gets previous day
 * @param  {Date}    date
 * @return {Date}
 */
export const getPreviousDay = (date) => {
  const previousDay = new Date(date.getTime());

  previousDay.setDate(date.getDate() - 1);

  return previousDay;
};

/**
 * Safely parses a plain YYYY-MM-DD string into a Date
 * without timezone shift.
 * @param {string} dateString - in format YYYY-MM-DD
 * @returns {Date}
 */
export const parseLocalDate = (dateString) => {
  if (!dateString || typeof dateString !== 'string') return null;

  const [y, m, d] = dateString.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const parseDateBoundary = (date) => {
  if (!date) return null;
  if (date instanceof Date) return isNaN(date.getTime()) ? null : date;

  const parsed = parseLocalDate(date);
  return parsed && !isNaN(parsed.getTime()) ? parsed : null;
};

/**
 * checks whether a date respects optional min/max boundaries.
 * @param  {Object} options
 * @param  {Date|null} options.date
 * @param  {Date|String|null} options.maxDate
 * @param  {Date|String|null} options.minDate
 * @return {Boolean}
 */
export const isDateWithinBounds = ({ date, maxDate, minDate }) => {
  if (!date) return true;

  const max = parseDateBoundary(maxDate);
  const min = parseDateBoundary(minDate);

  if (min && date < min) return false;
  if (max && date > max) return false;

  return true;
};

/**
 * checks if a date (YYYY-MM-DD) has started (is today or in the past)
 * @param  {String} dateString - in format YYYY-MM-DD
 * @return {Boolean}
 */
export const hasStarted = (dateString) => {
  if (!dateString) return false;
  const today = new Date().toISOString().split('T')[0];
  return dateString <= today;
};
